// /projects/:id — project detail page with Scans, Branches, Pull Requests,
// and Permissions tabs (T031, T058, T175). Branch switcher added in T058.
// Permissions tab (T175) added for admin_project + org_admin users.
// T176: "Your access" pill row in the header shows the 5 project-scoped roles
// the current user holds. Admin actions (edit/delete project, Permissions tab)
// are hidden for users lacking admin_project.
//
// Composition:
//   - Header: project name + slug chip + branch switcher (T058) + optional
//     repo URL. Project is fetched via SWR; while it loads we render a slim
//     skeleton so the page doesn't flash an empty header.
//   - Tab strip: "Scans", "Branches", "Pull Requests", "Permissions". Active
//     tab is mirrored in `?tab=…` (default: scans) for refresh- and
//     share-stable deep links.
//   - Tab body: each tab owns its own data fetch. Exception: branches are
//     fetched once at page level so the header BranchSwitcher and the Branches
//     tab share the same SWR cache entry (no double-fetch).
//
// Branch switcher (T058):
//   Selected branch is persisted in `?branch=<branchId>` so refresh, share,
//   and the back button all return to the same branch context. When a branch
//   is selected the "View issues" link is scoped to that branch; the Branches
//   tab highlights the active row.

import { FormEvent, ReactNode, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import useSWR, { mutate as swrMutate } from 'swr';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Bug,
  Check,
  ChevronDown,
  ChevronRight,
  ClipboardCopy,
  Container,
  Copy,
  Download,
  Eye,
  ExternalLink,
  FileCode2,
  FolderTree,
  GitBranch,
  GitMerge,
  GitPullRequest,
  KeyRound,
  Loader2,
  Lock,
  Plus,
  Radar,
  Settings,
  ShieldAlert,
  Star,
  Trash2,
  UserPlus,
  Users,
  Wrench,
  X,
} from 'lucide-react';
import {
  AdminUser,
  ApiError,
  Branch,
  DuplicatedBlockGroup,
  HotspotListParams,
  HotspotListResponse,
  Issue,
  IssueListResponse,
  MyProjectPermissions,
  ProjectGrantEntry,
  ProjectPermissionRole,
  Project,
  ProjectPatch,
  ProjectQualityProfileSummary,
  ProjectToken,
  PullRequest,
  QGMetrics,
  QualityGateConditionStatus,
  QualityGateSummary,
  Scan,
  ScanDuplications,
  ScanMeasures,
  adminUsersApi,
  buildHotspotsURL,
  buildIssuesURL,
  downloadReport,
  fetcher,
  projectPermissionsApi,
  projectTokensApi,
  projectsApi,
  scanApi,
} from '../lib/api';
import { IssueDetailDrawer } from '../components/IssueDetailDrawer';
import { PageHeader, Table, TBody, TD, TH, THead, TR } from '../components/ui';
import { parseScaMessage } from '../lib/scaMessage';
import { safeHref } from '../lib/safeHref'

type TabKey = 'scans' | 'branches' | 'prs' | 'images' | 'permissions' | 'settings';
const BASE_TABS: { key: TabKey; label: string; adminOnly?: boolean }[] = [
  { key: 'scans', label: 'Scans' },
  { key: 'branches', label: 'Branches' },
  { key: 'prs', label: 'Pull Requests' },
  { key: 'images', label: 'Images' },
  { key: 'permissions', label: 'Permissions', adminOnly: true },
  { key: 'settings', label: 'Settings', adminOnly: true },
];

function parseTabKey(raw: string | null): TabKey {
  if (raw === 'branches') return 'branches';
  if (raw === 'prs') return 'prs';
  if (raw === 'images') return 'images';
  if (raw === 'permissions') return 'permissions';
  if (raw === 'settings') return 'settings';
  return 'scans';
}

function formatErrorMessage(e: unknown, fallback = 'Request failed'): string {
  if (e instanceof ApiError) return `${e.code}: ${e.message}`;
  if (e instanceof Error) return e.message;
  return fallback;
}

export function ProjectDetail() {
  const { projectID } = useParams<{ projectID: string }>();
  const id = projectID ?? '';

  // useSearchParams gives us `?tab=…` round-tripping without forcing the
  // rest of the page to re-render on every tab switch — only the tab body
  // re-renders because it keys off `tab`.
  const [search, setSearch] = useSearchParams();
  const tab = parseTabKey(search.get('tab'));

  const setTab = (next: TabKey) => {
    const params = new URLSearchParams(search);
    if (next === 'scans') {
      // Drop the query for the default tab so links stay clean.
      params.delete('tab');
    } else {
      params.set('tab', next);
    }
    setSearch(params, { replace: true });
  };

  // Branch selection — persisted in ?branch=<id> so refresh, share, and the
  // back button all return to the same branch context (T058).
  const selectedBranchId = search.get('branch') ?? '';
  const setSelectedBranchId = (branchId: string) => {
    const params = new URLSearchParams(search);
    if (branchId) params.set('branch', branchId);
    else params.delete('branch');
    setSearch(params, { replace: true });
  };

  // SWR key is the URL itself — caches survive tab switches in the same
  // session, so flipping Scans → Branches → Scans is instant the second
  // time around.
  const projectKey = id ? `/api/v1/projects/${id}` : null;
  const project = useSWR<Project>(projectKey, fetcher);

  const gateKey = id ? `/api/v1/projects/${id}/quality-gate` : null;
  const gate = useSWR<QualityGateSummary>(gateKey, fetcher);

  // T913: which quality profile is bound + rule-catalog coverage, so the
  // issue counts on this page can be explained rather than taken on faith.
  const profileSummaryKey = id ? `/api/v1/projects/${id}/quality-profile` : null;
  const profileSummary = useSWR<ProjectQualityProfileSummary>(profileSummaryKey, fetcher);

  // Branches are fetched at page level so the header BranchSwitcher and the
  // Branches tab share the same SWR cache entry and don't double-fetch.
  const branchesKey = id ? `/api/v1/projects/${id}/branches` : null;
  const branchesResult = useSWR<Branch[]>(branchesKey, fetcher);

  // T176: fetch the current user's project-scoped roles. Used to show the
  // "Your access" pill row and to conditionally render admin actions.
  // Errors are silently ignored — the pill row just won't render in that case.
  const myPermsKey = id ? `/api/v1/projects/${id}/my-permissions` : null;
  const myPermsResult = useSWR<MyProjectPermissions>(myPermsKey, fetcher);
  const myRoles = new Set<ProjectPermissionRole>(myPermsResult.data?.roles ?? []);
  const canAdminProject = myRoles.has('admin_project');

  // Only show the Permissions tab to users who have admin_project (or received
  // all roles from the org_admin short-circuit path). Users without the role
  // see the pill row showing they lack it, which self-explains the missing tab.
  const visibleTabs = BASE_TABS.filter((t) => !t.adminOnly || canAdminProject);

  return (
    <div className="max-w-5xl mx-auto p-8 w-full">
      <BackLink />
      <ProjectHeader
        isLoading={project.isLoading}
        error={project.error}
        project={project.data}
        projectID={id}
        branches={branchesResult.data}
        selectedBranchId={selectedBranchId}
        onBranchChange={setSelectedBranchId}
        myRoles={myRoles}
      />
      <QualityGatePanel isLoading={gate.isLoading} data={gate.data} />
      <QualityProfilePanel
        projectID={id}
        isLoading={profileSummary.isLoading}
        data={profileSummary.data}
        canAdminProject={canAdminProject}
        onChanged={() => { void swrMutate(profileSummaryKey); }}
      />
      {gate.data?.scan_id ? (
        <MeasuresDashboard scanID={gate.data.scan_id} />
      ) : null}
      {gate.data?.scan_id ? (
        <SmellDensityPanel projectID={id} branchId={selectedBranchId} />
      ) : null}
      <TabStrip active={tab} tabs={visibleTabs} onChange={setTab} />
      <div className="mt-6">
        {tab === 'scans' && <ScansTab projectID={id} />}
        {tab === 'branches' && (
          <BranchesTab
            projectID={id}
            defaultBranch={project.data?.default_branch}
            selectedBranchId={selectedBranchId}
          />
        )}
        {tab === 'prs' && <PullRequestsTab projectID={id} />}
        {tab === 'images' && (
          <ImagesTab projectID={id} selectedBranchId={selectedBranchId} />
        )}
        {tab === 'permissions' && <PermissionsTab projectID={id} />}
        {tab === 'settings' && (
          <SettingsTab projectID={id} project={project.data} projectKey={projectKey} />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function BackLink() {
  return (
    <Link
      to="/projects"
      className="
        inline-flex items-center gap-1.5 mb-5 text-xs font-semibold
        uppercase tracking-[0.08em] text-fg-muted hover:text-fg
        transition-colors
      "
    >
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
      <span>All projects</span>
    </Link>
  );
}

interface ProjectHeaderProps {
  readonly isLoading: boolean;
  readonly error: unknown;
  readonly project?: Project;
  readonly projectID: string;
  /** Available branches — populates the branch switcher. Undefined while loading. */
  readonly branches?: Branch[];
  /** ID of the currently selected branch, or '' for all/default. */
  readonly selectedBranchId: string;
  readonly onBranchChange: (id: string) => void;
  /** T176: roles the current user holds on this project. Empty set while loading. */
  readonly myRoles: Set<ProjectPermissionRole>;
}

function ProjectHeader({
  isLoading,
  error,
  project,
  projectID,
  branches,
  selectedBranchId,
  onBranchChange,
  myRoles,
}: ProjectHeaderProps) {
  if (isLoading) {
    return (
      <header className="mb-6">
        <p className="orb-eyebrow mb-1">orbital · project</p>
        <div className="h-9 w-64 bg-bg-inset rounded animate-pulse" />
      </header>
    );
  }
  if (error) {
    return (
      <div className="mb-6">
        <PageHeader eyebrow="orbital · project" title="Project unavailable" />
        <ProjectErrorHint error={error} />
      </div>
    );
  }
  if (!project) {
    return null;
  }

  // Build the issues link. When a branch is selected, scope the link so
  // the issues page opens already filtered to that branch.
  const issuesHref = selectedBranchId
    ? `/projects/${projectID}/issues?branch=${encodeURIComponent(selectedBranchId)}`
    : `/projects/${projectID}/issues`;

  const canAdminProject = myRoles.has('admin_project');

  return (
    <PageHeader
      eyebrow="orbital · project"
      title={project.name}
      meta={<span className="orb-code text-xs">{project.slug}</span>}
      description={
        <>
          <span className="flex flex-wrap items-center gap-3 text-xs text-fg-muted">
            {branches && branches.length > 0 ? (
              <BranchSwitcher
                branches={branches}
                selectedId={selectedBranchId}
                onChange={onBranchChange}
              />
            ) : (
              <span
                className="inline-flex items-center gap-1"
                title="Default branch"
              >
                <GitBranch className="h-3.5 w-3.5" aria-hidden="true" />
                {project.default_branch}
              </span>
            )}
            {project.repo_url ? (
              <a
                href={safeHref(project.repo_url)}
                target="_blank"
                rel="noreferrer"
                className="
                  inline-flex items-center gap-1 hover:text-fg transition-colors
                "
              >
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="truncate max-w-[24rem]">{project.repo_url}</span>
              </a>
            ) : null}
          </span>
          {/* T176: "Your access" pill row — only shown once the roles fetch resolves. */}
          {myRoles.size > 0 ? (
            <YourAccessBar myRoles={myRoles} />
          ) : null}
        </>
      }
      actions={
        <>
          {/* T176: project settings (edit/delete) only visible to admin_project holders. */}
          {canAdminProject ? (
            <Link
              to={`/projects/${projectID}?tab=permissions`}
              title="Project settings and permissions"
              className="
                inline-flex items-center justify-center h-9 w-9 rounded-md
                text-fg-muted border border-border
                hover:text-fg hover:border-border-strong hover:bg-bg-hover
                transition-colors
              "
              aria-label="Project settings"
            >
              <Settings className="h-4 w-4" aria-hidden="true" />
            </Link>
          ) : null}
          <HotspotsToReviewLink projectID={projectID} />
          {/* Issues is a sibling page (T032). We expose it from the header so the
              ProjectDetail tab strip stays focused on Scans/Branches, and so the
              "where are my findings" CTA is the most visible action on the page. */}
          <Link
            to={issuesHref}
            className="
              inline-flex items-center gap-2 px-4 py-2 rounded-md
              text-xs font-semibold uppercase tracking-[0.08em]
              bg-accent text-accent-fg hover:opacity-90
              border border-accent/40 transition-opacity
            "
          >
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
            <span>View issues</span>
          </Link>
        </>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Hotspots-to-review link (T341) — surfaces the count of unreviewed Security
// Hotspots so reviewers know there's work waiting without having to open the
// dedicated Hotspots view first. Fetches up to MaxHotspotListLimit (200)
// to-review items; beyond that we show "200+" rather than paging further
// just to count — the Hotspots page itself paginates properly.
// ---------------------------------------------------------------------------

function HotspotsToReviewLink({ projectID }: { readonly projectID: string }) {
  const params: HotspotListParams = { project_id: projectID, status: 'to_review', limit: 200 };
  const key = projectID ? buildHotspotsURL(params) : null;
  const { data } = useSWR<HotspotListResponse>(key, fetcher);

  if (!data || data.items.length === 0) return null;
  const count = data.items.length;
  const suffix = data.next_cursor ? '+' : '';

  return (
    <Link
      to={`/projects/${projectID}/hotspots?status=to_review`}
      className="
        inline-flex items-center gap-2 px-4 py-2 rounded-md
        text-xs font-semibold uppercase tracking-[0.08em]
        bg-warning/10 text-warning hover:bg-warning/20
        border border-warning/40 transition-colors
      "
      title="Security hotspots awaiting review"
    >
      <Eye className="h-3.5 w-3.5" aria-hidden="true" />
      <span>{count}{suffix} hotspots to review</span>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Your access bar (T176)
// ---------------------------------------------------------------------------

const YOUR_ACCESS_ROLES: { key: ProjectPermissionRole; label: string; abbr: string }[] = [
  { key: 'browse', label: 'Browse — view issues, measures, comment', abbr: 'Browse' },
  { key: 'view_source', label: 'View Source — read source excerpts', abbr: 'View Source' },
  { key: 'admin_issues', label: 'Administer Issues — resolve, change severity, mark false-positive', abbr: 'Admin Issues' },
  { key: 'admin_project', label: 'Administer — project settings, integrations, delete', abbr: 'Admin Project' },
  { key: 'execute_analysis', label: 'Execute Analysis — push scan results (CI service accounts)', abbr: 'Exec Analysis' },
];

function YourAccessBar({ myRoles }: { readonly myRoles: Set<ProjectPermissionRole> }) {
  return (
    <span className="mt-3 flex items-center gap-2 flex-wrap">
      <span className="text-xs text-fg-subtle whitespace-nowrap">Your access:</span>
      {YOUR_ACCESS_ROLES.map((r) => {
        const held = myRoles.has(r.key);
        return (
          <span
            key={r.key}
            title={r.label}
            className={`orb-badge border ${
              held
                ? 'bg-accent-muted text-accent border-accent/30'
                : 'bg-bg-inset text-fg-subtle border-border opacity-50'
            }`}
          >
            {r.abbr}
          </span>
        );
      })}
    </span>
  );
}

function ProjectErrorHint({ error }: { readonly error: unknown }) {
  if (error instanceof ApiError && error.status === 404) {
    return (
      <div role="alert" className="orb-card p-5 border-danger/40 bg-danger/5">
        <p className="text-sm text-fg-muted">
          No such project (or it belongs to a different org).
        </p>
      </div>
    );
  }
  let msg = 'request failed';
  if (error instanceof ApiError) {
    msg = `${error.code}: ${error.message}`;
  } else if (error instanceof Error) {
    msg = error.message;
  }
  return (
    <div role="alert" className="orb-card p-5 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-1">request failed</p>
      <p className="text-sm text-fg">{msg}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Branch switcher (T058)
// ---------------------------------------------------------------------------

interface BranchSwitcherProps {
  readonly branches: Branch[];
  /** Currently selected branch ID, or '' for "all / default". */
  readonly selectedId: string;
  readonly onChange: (id: string) => void;
}

function BranchSwitcher({ branches, selectedId, onChange }: BranchSwitcherProps) {
  // Main/default branch first, then alphabetical.
  const sorted = [...branches].sort((a, b) => {
    if (a.is_main !== b.is_main) return a.is_main ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <label className="inline-flex items-center gap-1.5 cursor-pointer">
      <GitBranch className="h-3.5 w-3.5 shrink-0 text-fg-subtle" aria-hidden="true" />
      <select
        value={selectedId}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Switch branch"
        className="
          bg-bg-elevated text-xs text-fg-muted border border-border rounded
          px-2 py-0.5 cursor-pointer
          hover:border-border-strong hover:text-fg
          focus:outline-none focus:ring-1 focus:ring-accent/50
          max-w-[20rem]
        "
      >
        <option value="">All branches</option>
        {sorted.map((b) => (
          <option key={b.id} value={b.id}>
            {b.name}{b.is_main ? ' (default)' : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Quality gate badge + breakdown (T056)
// ---------------------------------------------------------------------------

interface QualityGatePanelProps {
  readonly isLoading: boolean;
  readonly data?: QualityGateSummary;
}

function QualityGatePanel({ isLoading, data }: QualityGatePanelProps) {
  if (isLoading) {
    return (
      <div className="mb-6 orb-card p-5 flex items-center gap-3 text-fg-muted">
        <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden="true" />
        <span className="text-sm">Loading quality gate…</span>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="mb-6 orb-card p-5">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <span className="orb-eyebrow">Quality Gate</span>
          <GateBadge status={data.gate_status} />
        </div>
        {data.scanned_at ? (
          <span className="text-xs text-fg-subtle" data-testid="last-scan-timestamp">
            Last scan {formatTimestamp(data.scanned_at)}
          </span>
        ) : null}
      </div>
      {data.metrics && (
        <MetricsBreakdown metrics={data.metrics} scanID={data.scan_id} />
      )}
      {!data.metrics && data.gate_status === 'not_run' && (
        <p className="text-sm text-fg-muted">
          No succeeded scan yet — run{' '}
          <span className="orb-code">orbital-lint scanner run</span> to compute
          the gate.
        </p>
      )}
      {data.conditions && data.conditions.length > 0 && (
        <ConditionsList gateName={data.gate_name} conditions={data.conditions} />
      )}
    </div>
  );
}

// T913: which quality profile is bound to this project, and how much of its
// own (language-scoped) rule catalog it activates — so a curated-down count
// can be explained rather than taken on faith. active_rule_count/
// catalog_rule_count is a rule-catalog-coverage proxy, not a live
// findings-suppressed count: a rule excluded from the profile never runs, so
// there's nothing persisted to count for it (see ProjectQualityProfileSummary's
// doc comment in api.ts, which also covers the language-scoping caveat).
interface QualityProfilePanelProps {
  readonly projectID: string;
  readonly isLoading: boolean;
  readonly data: ProjectQualityProfileSummary | undefined;
  readonly canAdminProject: boolean;
  readonly onChanged: () => void;
}

function QualityProfilePanel({
  projectID,
  isLoading,
  data,
  canAdminProject,
  onChanged,
}: QualityProfilePanelProps) {
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="mb-6 orb-card p-5 flex items-center gap-3 text-fg-muted">
        <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden="true" />
        <span className="text-sm">Loading quality profile…</span>
      </div>
    );
  }
  if (!data) return null;

  const suppressedCount = Math.max(0, data.catalog_rule_count - data.active_rule_count);
  const isUnrestricted = !data.profile_id;

  const handleShowEverything = async () => {
    if (
      !confirm(
        'Clear this project’s quality profile override? Future scans fall ' +
          'back to the org default profile, or run every catalog rule if the ' +
          'org has none configured either — this can increase finding volume. ' +
          'You can rebind a profile afterwards from Settings.',
      )
    ) {
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      await projectsApi.patch(projectID, { quality_profile_id: '' });
      onChanged();
    } catch (e) {
      setActionError(formatErrorMessage(e, 'Failed to clear the quality profile'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-6 orb-card p-5" data-testid="quality-profile-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="orb-eyebrow">Quality Profile</span>
          <span className="text-sm font-medium text-fg" data-testid="quality-profile-name">
            {data.profile_name}
          </span>
          {data.is_system_profile ? (
            <span className="text-xs text-fg-subtle">(built-in)</span>
          ) : null}
        </div>
        {!isUnrestricted && canAdminProject ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => { void handleShowEverything(); }}
            data-testid="show-everything-btn"
            className="
              inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
              text-xs font-semibold uppercase tracking-[0.08em]
              border border-border text-fg-muted
              hover:text-fg hover:border-border-strong transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            {busy ? 'Clearing…' : 'Show everything'}
          </button>
        ) : null}
      </div>
      <p className="text-sm text-fg-muted mt-2" data-testid="quality-profile-coverage">
        {data.active_rule_count.toLocaleString()} of {data.catalog_rule_count.toLocaleString()}{' '}
        rules active
        {suppressedCount > 0
          ? ` — ${suppressedCount.toLocaleString()} rules not active for this project.`
          : '.'}
      </p>
      <p className="text-xs text-fg-subtle mt-1">
        Counts rules for this profile{isUnrestricted ? '' : '’s language'}, not live
        findings — an inactive rule never runs, so its findings are never
        computed. Browse the full catalog on the Rules page.
      </p>
      {actionError ? (
        <p className="text-xs text-danger mt-2" role="alert">
          {actionError}
        </p>
      ) : null}
    </div>
  );
}

// T530: per-condition breakdown of the resolved gate, so a failed gate_status
// can be traced to exactly which condition(s) tripped it.
const CONDITION_OP_SYMBOL: Record<QualityGateConditionStatus['op'], string> = {
  GT: '>',
  GTE: '≥',
  LT: '<',
  LTE: '≤',
  EQ: '=',
};

function formatMetricLabel(metric: string): string {
  return metric
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatConditionNumber(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

interface ConditionRowProps {
  readonly condition: QualityGateConditionStatus;
}

function ConditionRow({ condition }: ConditionRowProps) {
  const { metric, op, threshold, on_new_code: onNewCode, actual, missing, passed } = condition;
  let icon: ReactNode;
  let iconClass: string;
  if (passed) {
    icon = <Check className="h-3.5 w-3.5" aria-hidden="true" />;
    iconClass = 'text-accent';
  } else {
    icon = <X className="h-3.5 w-3.5" aria-hidden="true" />;
    iconClass = 'text-danger';
  }
  return (
    <li className="flex items-center gap-2 py-1.5 text-sm">
      <span className={`shrink-0 ${iconClass}`}>{icon}</span>
      <span className="text-fg">{formatMetricLabel(metric)}</span>
      {onNewCode ? <span className="text-xs text-fg-subtle">(new code)</span> : null}
      <span className="text-fg-muted">
        {CONDITION_OP_SYMBOL[op]} {formatConditionNumber(threshold)}
      </span>
      <span className={`ml-auto text-xs ${passed ? 'text-fg-muted' : 'text-danger'}`}>
        {missing ? 'no data' : `actual: ${formatConditionNumber(actual)}`}
      </span>
    </li>
  );
}

interface ConditionsListProps {
  readonly gateName?: string;
  readonly conditions: QualityGateConditionStatus[];
}

function ConditionsList({ gateName, conditions }: ConditionsListProps) {
  const failedCount = conditions.filter((c) => !c.passed).length;
  return (
    <div className="mt-4 pt-4 border-t border-border">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-xs font-semibold text-fg-subtle uppercase tracking-wide">
          {gateName ? `Conditions — ${gateName}` : 'Conditions'}
        </span>
        {failedCount > 0 ? (
          <span className="text-xs text-danger">{failedCount} failing</span>
        ) : null}
      </div>
      <ul className="divide-y divide-border">
        {conditions.map((c) => (
          <ConditionRow key={`${c.metric}-${c.on_new_code ? 'new' : 'overall'}`} condition={c} />
        ))}
      </ul>
    </div>
  );
}

function GateBadge({ status }: { readonly status: 'pass' | 'fail' | 'not_run' }) {
  let tone: string;
  if (status === 'pass') {
    tone = 'bg-accent-muted text-accent border-accent/30';
  } else if (status === 'fail') {
    tone = 'bg-danger/10 text-danger border-danger/30';
  } else {
    tone = 'bg-bg-inset text-fg-muted border-border';
  }
  let label: string;
  if (status === 'pass') {
    label = 'PASSED';
  } else if (status === 'fail') {
    label = 'FAILED';
  } else {
    label = 'NOT RUN';
  }
  return (
    <span
      className={`inline-flex items-center px-3 py-0.5 rounded text-xs font-bold tracking-widest uppercase border ${tone}`}
    >
      {label}
    </span>
  );
}

interface MetricCardProps {
  readonly icon: ReactNode;
  readonly label: string;
  readonly value: number;
  readonly newValue?: number;
  readonly danger?: boolean;
}

function MetricCard({ icon, label, value, newValue, danger }: MetricCardProps) {
  let valueTone: string;
  if (danger && value > 0) {
    valueTone = 'text-danger';
  } else if (value === 0) {
    valueTone = 'text-fg-muted';
  } else {
    valueTone = 'text-fg';
  }
  return (
    <div className="flex flex-col gap-1 p-3 rounded-md bg-bg-inset border border-border">
      <div className="flex items-center gap-1.5 text-fg-subtle text-xs">
        <span className="h-3.5 w-3.5 shrink-0" aria-hidden="true">
          {icon}
        </span>
        <span className="truncate">{label}</span>
      </div>
      <span className={`font-display text-xl font-semibold ${valueTone}`}>
        {value}
      </span>
      {newValue !== undefined && newValue > 0 ? (
        <span className="text-xs text-warning">+{newValue} new</span>
      ) : null}
    </div>
  );
}

function MetricsBreakdown({ metrics, scanID }: { readonly metrics: QGMetrics; readonly scanID?: string }) {
  const cards: MetricCardProps[] = [
    {
      icon: <Bug className="h-3.5 w-3.5" />,
      label: 'Bugs',
      value: metrics.bugs,
      newValue: metrics.new_bugs,
      danger: true,
    },
    {
      icon: <ShieldAlert className="h-3.5 w-3.5" />,
      label: 'Vulnerabilities',
      value: metrics.vulnerabilities,
      newValue: metrics.new_vulnerabilities,
      danger: true,
    },
    {
      icon: <Wrench className="h-3.5 w-3.5" />,
      label: 'Code Smells',
      value: metrics.code_smells,
      newValue: metrics.new_code_smells,
    },
    {
      icon: <AlertTriangle className="h-3.5 w-3.5" />,
      label: 'Hotspots',
      value: metrics.security_hotspots,
      newValue: metrics.new_security_hotspots,
      danger: true,
    },
    {
      icon: <KeyRound className="h-3.5 w-3.5" />,
      label: 'Secrets',
      value: metrics.secrets,
      newValue: metrics.new_secrets,
      danger: true,
    },
    {
      icon: <FileCode2 className="h-3.5 w-3.5" />,
      label: 'LOC',
      value: metrics.loc,
    },
  ];

  // T204: coverage cards — only shown when a coverage report was uploaded
  // (lines_to_cover > 0 distinguishes "no data" from "0% coverage").
  const hasCoverage =
    metrics.lines_to_cover !== undefined && metrics.lines_to_cover > 0;
  // new_coverage is shown when the scan was a PR scan with per-line data:
  // if new_lines_to_cover > 0, this is real data; if the gate sentinel
  // 100.0 is emitted by Flatten, new_lines_to_cover is 0, so we hide it.
  const hasNewCoverage =
    metrics.new_lines_to_cover !== undefined && metrics.new_lines_to_cover > 0;

  // T205/T206: duplication — show when CPD produced data (duplicated_blocks > 0).
  const hasDuplication =
    metrics.duplicated_blocks !== undefined && metrics.duplicated_blocks > 0;
  const hasNewDuplication =
    metrics.new_duplicated_lines !== undefined && metrics.new_duplicated_lines > 0;

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {cards.map((c) => (
          <MetricCard key={c.label} {...c} />
        ))}
      </div>
      {(hasCoverage || hasNewCoverage) && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {hasCoverage && (
            <CoverageBadge
              label="Coverage"
              pct={metrics.coverage ?? 0}
              linesToCover={metrics.lines_to_cover ?? 0}
              uncovered={metrics.uncovered_lines ?? 0}
            />
          )}
          {hasNewCoverage && (
            <CoverageBadge
              label="New Code Coverage"
              pct={metrics.new_coverage ?? 0}
              linesToCover={metrics.new_lines_to_cover ?? 0}
              uncovered={metrics.new_uncovered_lines ?? 0}
              isNew
            />
          )}
        </div>
      )}
      {(hasDuplication || hasNewDuplication) && (
        <DuplicationSection
          metrics={metrics}
          scanID={scanID}
          hasNew={hasNewDuplication}
        />
      )}
    </div>
  );
}

interface CoverageBadgeProps {
  readonly label: string;
  readonly pct: number;
  readonly linesToCover: number;
  readonly uncovered: number;
  readonly isNew?: boolean;
}

function CoverageBadge({ label, pct, linesToCover, uncovered, isNew }: CoverageBadgeProps) {
  const covered = linesToCover - uncovered;
  let tone: string;
  if (pct >= 80) {
    tone = 'text-accent';
  } else if (pct >= 60) {
    tone = 'text-warning';
  } else {
    tone = 'text-danger';
  }
  return (
    <div
      className={`flex flex-col gap-1 p-3 rounded-md bg-bg-inset border ${isNew ? 'border-accent/30' : 'border-border'}`}
    >
      <div className="flex items-center gap-1.5 text-fg-subtle text-xs">
        <Radar className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate">{label}</span>
      </div>
      <span className={`font-display text-xl font-semibold ${tone}`}>
        {pct.toFixed(1)}%
      </span>
      <span className="text-xs text-fg-muted">
        {covered}/{linesToCover} lines
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Duplication section (T205/T206)
// ---------------------------------------------------------------------------

interface DuplicationSectionProps {
  readonly metrics: QGMetrics;
  readonly scanID?: string;
  readonly hasNew: boolean;
}

function DuplicationSection({ metrics, scanID, hasNew }: DuplicationSectionProps) {
  const [showDrilldown, setShowDrilldown] = useState(false);
  const density = metrics.duplicated_lines_density ?? 0;
  const newDensity = metrics.new_duplicated_lines_density ?? 0;
  let tone: string;
  if (density < 3) {
    tone = 'text-accent';
  } else if (density < 10) {
    tone = 'text-warning';
  } else {
    tone = 'text-danger';
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="flex flex-col gap-1 p-3 rounded-md bg-bg-inset border border-border">
          <div className="flex items-center gap-1.5 text-fg-subtle text-xs">
            <Copy className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="truncate">Duplication</span>
          </div>
          <span className={`font-display text-xl font-semibold ${tone}`}>
            {density.toFixed(1)}%
          </span>
          <span className="text-xs text-fg-muted">
            {metrics.duplicated_lines ?? 0} dup. lines · {metrics.duplicated_blocks ?? 0} blocks
          </span>
        </div>
        {hasNew && (
          <div className="flex flex-col gap-1 p-3 rounded-md bg-bg-inset border border-accent/30">
            <div className="flex items-center gap-1.5 text-fg-subtle text-xs">
              <Copy className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">New Code Dup.</span>
            </div>
            <span className={`font-display text-xl font-semibold ${dupClass(newDensity)}`}>
              {newDensity.toFixed(1)}%
            </span>
            <span className="text-xs text-fg-muted">
              {metrics.new_duplicated_lines ?? 0} new dup. lines
            </span>
          </div>
        )}
        {scanID && (metrics.duplicated_blocks ?? 0) > 0 && (
          <button
            type="button"
            onClick={() => setShowDrilldown((v) => !v)}
            className="
              flex items-center justify-center gap-1.5 p-3 rounded-md
              bg-bg-inset border border-border text-xs text-fg-muted
              hover:text-fg hover:border-border-strong transition-colors
              col-span-1
            "
            aria-expanded={showDrilldown}
          >
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform ${showDrilldown ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
            {showDrilldown ? 'Hide' : 'Show'} duplicated blocks
          </button>
        )}
      </div>
      {showDrilldown && scanID ? (
        <DuplicationDrilldown scanID={scanID} />
      ) : null}
    </div>
  );
}

function DuplicationDrilldown({ scanID }: { readonly scanID: string }) {
  const [data, setData] = useState<ScanDuplications | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // Fetch once on mount.
  useState(() => {
    scanApi.getDuplications(scanID)
      .then((d) => { setData(d); setLoading(false); })
      .catch((e: unknown) => {
        setErr(e instanceof Error ? e.message : 'load failed');
        setLoading(false);
      });
  });

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-fg-muted py-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        Loading duplication data…
      </div>
    );
  }
  if (err) {
    return <p className="text-xs text-danger py-2">{err}</p>;
  }
  if (!data || data.groups.length === 0) {
    return <p className="text-xs text-fg-muted py-2">No duplicated blocks to show.</p>;
  }

  return (
    <div className="space-y-2">
      <p className="orb-eyebrow">Duplicated block locations ({data.groups.length} groups)</p>
      <ul className="space-y-2">
        {data.groups.slice(0, 50).map((g, gi) => (
          // eslint-disable-next-line react/no-array-index-key
          <DuplicationGroupRow key={gi} group={g} index={gi} />
        ))}
      </ul>
      {data.groups.length > 50 && (
        <p className="text-xs text-fg-muted">
          Showing 50 of {data.groups.length} groups.
        </p>
      )}
    </div>
  );
}

function DuplicationGroupRow({ group, index }: { readonly group: DuplicatedBlockGroup; readonly index: number }) {
  return (
    <li className="orb-card p-3 text-xs">
      <p className="text-fg-subtle mb-1.5 font-semibold">Group {index + 1}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {group.blocks.map((b, bi) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={bi} className="flex items-center gap-2 p-2 rounded bg-bg-inset border border-border">
            <FileCode2 className="h-3.5 w-3.5 shrink-0 text-fg-subtle" aria-hidden="true" />
            <span className="truncate font-mono text-fg">{b.path}</span>
            <span className="ml-auto shrink-0 text-fg-muted whitespace-nowrap">
              L{b.start_line}–{b.end_line}
            </span>
          </div>
        ))}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Tab strip
// ---------------------------------------------------------------------------

interface TabStripProps {
  readonly active: TabKey;
  readonly tabs: { key: TabKey; label: string }[];
  readonly onChange: (key: TabKey) => void;
}

function TabStrip({ active, tabs, onChange }: TabStripProps) {
  return (
    <div
      className="flex items-center gap-1 border-b border-border"
      role="tablist"
      aria-label="Project sections"
    >
      {tabs.map((t) => {
        const isActive = t.key === active;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`panel-${t.key}`}
            id={`tab-${t.key}`}
            onClick={() => onChange(t.key)}
            className={[
              'orb-tab -mb-px border-b-2',
              isActive
                ? 'text-accent border-accent'
                : 'text-fg-muted border-transparent hover:text-fg',
            ].join(' ')}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scans tab
// ---------------------------------------------------------------------------

function ScansTab({ projectID }: { readonly projectID: string }) {
  const { data, error, isLoading } = useSWR<Scan[]>(
    projectID ? `/api/v1/projects/${projectID}/scans` : null,
    fetcher,
  );
  return (
    <Panel id="panel-scans" labelledBy="tab-scans">
      {isLoading ? <Loading label="Loading scans…" /> : null}
      {error && !isLoading ? <RequestError error={error} /> : null}
      {!isLoading && !error && (!data || data.length === 0) ? (
        <EmptyState
          icon={<Radar className="h-5 w-5" aria-hidden="true" />}
          title="No scans yet."
          body={
            <>
              Run <span className="orb-code">orbital-lint scanner run</span> in
              CI or locally with this project&rsquo;s token to record the first
              scan. The Phase-1 list endpoint is a stub — scans will appear
              here once the backend wiring lands.
            </>
          }
        />
      ) : null}
      {!isLoading && !error && data && data.length > 0 ? (
        <ScansList scans={data} />
      ) : null}
    </Panel>
  );
}

function ScansList({ scans }: { readonly scans: Scan[] }) {
  // Newest first — matches the convention Projects.tsx uses and the
  // ordering the eventual ScansRepo.List will enforce server-side.
  const sorted = [...scans].sort((a, b) =>
    b.created_at.localeCompare(a.created_at),
  );
  return (
    <ul className="orb-card divide-y divide-border">
      {sorted.map((s) => (
        <li
          key={s.id}
          className="flex items-center gap-4 px-5 py-3 text-sm"
        >
          <span className="orb-code text-xs truncate">{s.id.slice(0, 8)}</span>
          <StatusChip status={s.status} />
          {s.gate_status ? <GateChip status={s.gate_status} /> : null}
          {s.trace_id ? (
            <span
              className="orb-code text-xs text-fg-subtle"
              title={`OpenTelemetry trace: ${s.trace_id}`}
            >
              trace:{s.trace_id.slice(0, 8)}
            </span>
          ) : null}
          <span className="ml-auto text-xs text-fg-subtle">
            {formatTimestamp(s.created_at)}
          </span>
          {s.status === 'succeeded' && (
            <button
              onClick={() => downloadReport(
                `/api/v1/scans/${encodeURIComponent(s.id)}/sbom`,
                `orbital-lint-sbom-${s.id.slice(0, 8)}.cdx.json`,
              )}
              title="Download CycloneDX SBOM"
              className="p-1 rounded hover:bg-bg-inset text-fg-muted hover:text-fg transition-colors"
            >
              <Download className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

function StatusChip({ status }: { readonly status: string }) {
  let tone: string;
  if (status === 'succeeded') {
    tone = 'bg-accent-muted text-accent border-accent/30';
  } else if (status === 'failed' || status === 'cancelled') {
    tone = 'bg-danger/10 text-danger border-danger/30';
  } else if (status === 'running') {
    tone = 'bg-warning/10 text-warning border-warning/30';
  } else {
    tone = 'bg-bg-inset text-fg-muted border-border';
  }
  return (
    <span className={`orb-badge border ${tone}`}>{status || 'unknown'}</span>
  );
}

function GateChip({ status }: { readonly status: string }) {
  let tone: string;
  if (status === 'pass') {
    tone = 'bg-accent-muted text-accent border-accent/30';
  } else if (status === 'fail') {
    tone = 'bg-danger/10 text-danger border-danger/30';
  } else {
    tone = 'bg-bg-inset text-fg-muted border-border';
  }
  return <span className={`orb-badge border ${tone}`}>gate · {status}</span>;
}

// ---------------------------------------------------------------------------
// Branches tab
// ---------------------------------------------------------------------------

interface BranchesTabProps {
  readonly projectID: string;
  /** When known, the row matching this name is decorated as "default". */
  readonly defaultBranch?: string;
  /** ID of the branch selected in the header switcher, or '' for none. */
  readonly selectedBranchId?: string;
}

function BranchesTab({ projectID, defaultBranch, selectedBranchId }: BranchesTabProps) {
  // The SWR cache is shared with the page-level branchesResult fetch, so
  // switching to this tab is instant on the second visit.
  const { data, error, isLoading } = useSWR<Branch[]>(
    projectID ? `/api/v1/projects/${projectID}/branches` : null,
    fetcher,
  );
  return (
    <Panel id="panel-branches" labelledBy="tab-branches">
      {isLoading ? <Loading label="Loading branches…" /> : null}
      {error && !isLoading ? <RequestError error={error} /> : null}
      {!isLoading && !error && (!data || data.length === 0) ? (
        <EmptyState
          icon={<GitBranch className="h-5 w-5" aria-hidden="true" />}
          title="No branches yet."
          body={
            <>
              Branches are materialized on the first scan against them. The
              project&rsquo;s default branch ({''}
              <span className="orb-code">{defaultBranch ?? 'main'}</span>)
              shows up here once it&rsquo;s been scanned.
            </>
          }
        />
      ) : null}
      {!isLoading && !error && data && data.length > 0 ? (
        <BranchesList
          branches={data}
          defaultBranch={defaultBranch}
          selectedBranchId={selectedBranchId}
        />
      ) : null}
    </Panel>
  );
}

function BranchesList({
  branches,
  defaultBranch,
  selectedBranchId,
}: {
  readonly branches: Branch[];
  readonly defaultBranch?: string;
  readonly selectedBranchId?: string;
}) {
  // Server already orders newest first; we still sort defensively so an
  // older API rev doesn't visually scramble the page.
  const sorted = [...branches].sort((a, b) => {
    if (a.is_main !== b.is_main) return a.is_main ? -1 : 1;
    return b.created_at.localeCompare(a.created_at);
  });
  return (
    <ul className="orb-card divide-y divide-border">
      {sorted.map((b) => {
        const isDefault =
          b.is_main || (defaultBranch !== undefined && defaultBranch === b.name);
        const isSelected = selectedBranchId ? b.id === selectedBranchId : false;
        return (
          <li
            key={b.id}
            className={[
              'flex items-center gap-4 px-5 py-3 text-sm',
              isSelected ? 'bg-bg-hover' : '',
            ].join(' ')}
          >
            <GitBranch
              className="h-4 w-4 text-fg-subtle"
              aria-hidden="true"
            />
            <span className="font-display text-sm text-fg truncate">
              {b.name}
            </span>
            {isDefault ? (
              <span
                className="orb-badge bg-accent-muted text-accent border border-accent/30"
                title="Project's default branch"
              >
                <Star className="h-3 w-3" aria-hidden="true" />
                default
              </span>
            ) : null}
            {isSelected ? (
              <span
                className="orb-badge bg-accent/15 text-accent border border-accent/30"
                title="Currently viewing this branch"
              >
                viewing
              </span>
            ) : null}
            <span className="ml-auto text-xs text-fg-subtle">
              {formatTimestamp(b.created_at)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Pull Requests tab + decoration preview pane (T057)
// ---------------------------------------------------------------------------

function PullRequestsTab({ projectID }: { readonly projectID: string }) {
  const { data, error, isLoading } = useSWR<PullRequest[]>(
    projectID ? `/api/v1/projects/${projectID}/pull-requests` : null,
    fetcher,
  );
  return (
    <Panel id="panel-prs" labelledBy="tab-prs">
      {isLoading ? <Loading label="Loading pull requests…" /> : null}
      {error && !isLoading ? <RequestError error={error} /> : null}
      {!isLoading && !error && (!data || data.length === 0) ? (
        <EmptyState
          icon={<GitPullRequest className="h-5 w-5" aria-hidden="true" />}
          title="No pull requests yet."
          body={
            <>
              PR decoration is posted automatically when{' '}
              <span className="orb-code">orbital-lint scanner run</span> is
              triggered from a CI workflow on a pull request. GitHub check runs
              and GitLab inline discussions will appear here once the GitHub App
              or GitLab integration is configured for this project.
            </>
          }
        />
      ) : null}
      {!isLoading && !error && data && data.length > 0 ? (
        <PullRequestList prs={data} />
      ) : null}
    </Panel>
  );
}

function PullRequestList({ prs }: { readonly prs: PullRequest[] }) {
  return (
    <ul className="orb-card divide-y divide-border">
      {prs.map((pr) => (
        <PullRequestRow key={pr.id} pr={pr} />
      ))}
    </ul>
  );
}

function PullRequestRow({ pr }: { readonly pr: PullRequest }) {
  const [open, setOpen] = useState(false);
  const prURL = prExternalURL(pr);

  return (
    <li className="px-5 py-4">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
        aria-expanded={open}
      >
        <span className="mt-0.5 text-fg-subtle shrink-0">
          {pr.status === 'merged' ? (
            <GitMerge className="h-4 w-4 text-accent" aria-hidden="true" />
          ) : (
            <GitPullRequest
              className={[
                'h-4 w-4',
                pr.status === 'open' ? 'text-accent' : 'text-fg-muted',
              ].join(' ')}
              aria-hidden="true"
            />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <ProviderChip provider={pr.provider} />
            <span className="orb-code text-xs">#{pr.external_id}</span>
            {pr.title ? (
              <span className="text-sm text-fg truncate">{pr.title}</span>
            ) : null}
            <PRStatusChip status={pr.status} />
          </div>
          <div className="mt-1 flex items-center gap-3 text-xs text-fg-muted">
            <span title="Base branch">
              ← <span className="orb-code">{pr.base_branch}</span>
            </span>
            {prURL ? (
              <a
                href={safeHref(prURL)}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 hover:text-fg transition-colors"
                aria-label={`Open PR #${pr.external_id} on ${pr.provider}`}
              >
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
                View on {providerLabel(pr.provider)}
              </a>
            ) : null}
            <span className="ml-auto">{formatTimestamp(pr.created_at)}</span>
          </div>
        </div>
        <span className="mt-1 text-fg-subtle shrink-0" aria-hidden="true">
          {open ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </span>
      </button>

      {open ? <DecorationPreview pr={pr} prURL={prURL} /> : null}
    </li>
  );
}

// DecorationPreview renders a stylised mockup of the check run / discussion
// that was (or will be) posted to the SCM provider. The orbital-lint server
// posts this decoration via the GitHub Checks API (T047/T048) or the GitLab
// Discussions API (T049/T050) on scan completion. We show the expected shape
// here so teams can verify the integration is wired up correctly.
function DecorationPreview({
  pr,
  prURL,
}: {
  readonly pr: PullRequest;
  readonly prURL: string | null;
}) {
  const isGitHub = pr.provider === 'github';
  let conclusion: 'pending' | 'success' | 'neutral';
  if (pr.status === 'open') {
    conclusion = 'pending';
  } else if (pr.status === 'merged') {
    conclusion = 'success';
  } else {
    conclusion = 'neutral';
  }

  let conclusionTone: string;
  if (conclusion === 'success') {
    conclusionTone = 'text-accent';
  } else if (conclusion === 'pending') {
    conclusionTone = 'text-warning';
  } else {
    conclusionTone = 'text-fg-muted';
  }

  let conclusionLabel: string;
  if (conclusion === 'success') {
    conclusionLabel = 'All checks passed';
  } else if (conclusion === 'pending') {
    conclusionLabel = 'Check in progress';
  } else {
    conclusionLabel = 'Check complete';
  }

  return (
    <div className="mt-3 ml-7">
      <p className="orb-eyebrow mb-2">Decoration preview</p>
      <div className="rounded-md border border-border bg-bg-inset p-4 text-sm">
        {isGitHub ? (
          <GitHubCheckPreview
            pr={pr}
            prURL={prURL}
            conclusionTone={conclusionTone}
            conclusionLabel={conclusionLabel}
          />
        ) : (
          <GitLabDiscussionPreview
            pr={pr}
            prURL={prURL}
            conclusionTone={conclusionTone}
            conclusionLabel={conclusionLabel}
          />
        )}
      </div>
    </div>
  );
}

interface PreviewProps {
  readonly pr: PullRequest;
  readonly prURL: string | null;
  readonly conclusionTone: string;
  readonly conclusionLabel: string;
}

function GitHubCheckPreview({
  pr,
  prURL,
  conclusionTone,
  conclusionLabel,
}: PreviewProps) {
  const checkURL = prURL ? `${prURL}/checks` : null;
  return (
    <div className="flex items-start gap-3">
      <span className={`mt-0.5 shrink-0 font-bold ${conclusionTone}`}>✓</span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-fg">orbital-lint</span>
          <span className={`text-xs ${conclusionTone}`}>{conclusionLabel}</span>
        </div>
        <p className="text-xs text-fg-muted mt-0.5">
          Quality gate evaluation posted as a GitHub Check Run on{' '}
          <span className="orb-code">{pr.repo_full_name || '…'}</span> PR #
          {pr.external_id}. Annotations mark new issues inline on the diff.
        </p>
        {checkURL ? (
          <a
            href={safeHref(checkURL)}
            target="_blank"
            rel="noreferrer"
            className="mt-1.5 inline-flex items-center gap-1 text-xs text-accent hover:underline"
          >
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
            View checks on GitHub
          </a>
        ) : null}
      </div>
    </div>
  );
}

function GitLabDiscussionPreview({
  pr,
  prURL,
  conclusionTone,
  conclusionLabel,
}: PreviewProps) {
  return (
    <div className="flex items-start gap-3">
      <span className={`mt-0.5 shrink-0 font-bold ${conclusionTone}`}>✓</span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-fg">orbital-lint</span>
          <span className={`text-xs ${conclusionTone}`}>{conclusionLabel}</span>
        </div>
        <p className="text-xs text-fg-muted mt-0.5">
          Pipeline status and inline discussion comments posted to GitLab MR #
          {pr.external_id}. New issues appear as resolvable discussion threads
          on the diff.
        </p>
        {prURL ? (
          <a
            href={safeHref(prURL)}
            target="_blank"
            rel="noreferrer"
            className="mt-1.5 inline-flex items-center gap-1 text-xs text-accent hover:underline"
          >
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
            View MR on GitLab
          </a>
        ) : null}
      </div>
    </div>
  );
}

function ProviderChip({ provider }: { readonly provider: string }) {
  const label = providerLabel(provider);
  return (
    <span className="orb-badge bg-bg-inset text-fg-muted border border-border">
      {label}
    </span>
  );
}

function PRStatusChip({ status }: { readonly status: string }) {
  let tone: string;
  if (status === 'open') {
    tone = 'bg-accent-muted text-accent border-accent/30';
  } else if (status === 'merged') {
    tone = 'bg-accent/5 text-accent border-accent/20';
  } else {
    tone = 'bg-bg-inset text-fg-muted border-border';
  }
  return <span className={`orb-badge border ${tone}`}>{status}</span>;
}

/** Construct a direct link to the PR/MR on the SCM provider. Returns null when
 *  not enough info is available (e.g. unknown provider or missing repo name). */
function prExternalURL(pr: PullRequest): string | null {
  const { provider, external_id: num, repo_full_name: repo } = pr;
  if (!num) return null;
  switch (provider) {
    case 'github':
      if (!repo) return null;
      return `https://github.com/${repo}/pull/${num}`;
    case 'gitlab':
      if (!repo) return null;
      return `https://gitlab.com/${repo}/-/merge_requests/${num}`;
    case 'bitbucket':
      if (!repo) return null;
      return `https://bitbucket.org/${repo}/pull-requests/${num}`;
    case 'azure':
      return null;
    default:
      return null;
  }
}

function providerLabel(provider: string): string {
  switch (provider) {
    case 'github':
      return 'GitHub';
    case 'gitlab':
      return 'GitLab';
    case 'bitbucket':
      return 'Bitbucket';
    case 'azure':
      return 'Azure DevOps';
    default:
      return provider;
  }
}

// ---------------------------------------------------------------------------
// Images tab (T648) — Dockerfile/YAML base-image advisories (T646) and
// image-SBOM ingest findings (T647) are both "known-vulnerable OS/language
// package inside a container image", just discovered a different way
// (source-only heuristic vs. an uploaded SBOM), so they share one view.
// ---------------------------------------------------------------------------

const IMAGE_FINDING_RULE_IDS = ['sca:image-base', 'sca:image-base-unknown', 'container:vuln'];

const SEVERITY_RANK: Record<string, number> = {
  BLOCKER: 5,
  CRITICAL: 4,
  MAJOR: 3,
  MINOR: 2,
  INFO: 1,
};

function severityTone(sev: string): string {
  if (sev === 'BLOCKER') return 'bg-danger/20 text-danger border-danger/60';
  if (sev === 'CRITICAL') return 'bg-danger/10 text-danger border-danger/40';
  if (sev === 'MAJOR') return 'bg-warning/10 text-warning border-warning/40';
  if (sev === 'MINOR') return 'bg-accent-muted text-accent border-accent/30';
  return 'bg-bg-inset text-fg-muted border-border';
}

interface ImagesTabProps {
  readonly projectID: string;
  readonly selectedBranchId?: string;
}

function ImagesTab({ projectID, selectedBranchId }: ImagesTabProps) {
  // Drawer state is local to this tab (mirrors Issues.tsx's `?issue=`
  // pattern) rather than lifted to the page — every other ProjectDetail tab
  // already manages its own state independently, and only this tab needs a
  // drawer.
  const [search, setSearch] = useSearchParams();
  const selectedIssueID = search.get('issue') ?? '';
  const openIssue = (id: string) => {
    const params = new URLSearchParams(search);
    params.set('issue', id);
    setSearch(params);
  };
  const closeIssue = () => {
    if (!search.has('issue')) return;
    const params = new URLSearchParams(search);
    params.delete('issue');
    setSearch(params, { replace: true });
  };

  // Single page (limit=200, the API's max) rather than infinite scroll —
  // an MVP view for a finding class expected to be small relative to the
  // full issue list. `hasMore` still drives the "+" suffix so a project
  // with more than 200 image findings doesn't silently look complete.
  const url = projectID
    ? buildIssuesURL({
      project_id: projectID,
      branch_id: selectedBranchId || undefined,
      rule_ids: IMAGE_FINDING_RULE_IDS,
      limit: 200,
    })
    : null;
  const { data, error, isLoading } = useSWR<IssueListResponse>(url, fetcher);
  const issues = data?.items ?? [];
  const hasMore = Boolean(data?.next_cursor);

  const selectedIssue = issues.find((i) => i.id === selectedIssueID) ?? null;

  return (
    <Panel id="panel-images" labelledBy="tab-images">
      {isLoading ? <Loading label="Loading image findings…" /> : null}
      {error && !isLoading ? <RequestError error={error} /> : null}
      {!isLoading && !error && issues.length === 0 ? (
        <EmptyState
          icon={<Container className="h-5 w-5" aria-hidden="true" />}
          title="No image findings yet."
          body={
            <>
              Base-image advisories are emitted when a scan sees a
              Dockerfile <span className="orb-code">FROM</span> or
              Kubernetes/Compose <span className="orb-code">image:</span>{' '}
              reference. For a layer-aware scan, upload a CycloneDX SBOM via{' '}
              <span className="orb-code">POST /scans/&#123;id&#125;/image-sbom</span>.
            </>
          }
        />
      ) : null}
      {!isLoading && !error && issues.length > 0 ? (
        <>
          <ImageSeveritySummary issues={issues} hasMore={hasMore} />
          <ImagesList issues={issues} selectedID={selectedIssueID} onSelect={openIssue} />
        </>
      ) : null}

      <IssueDetailDrawer
        issue={selectedIssue}
        requestedID={selectedIssueID || undefined}
        onClose={closeIssue}
      />
    </Panel>
  );
}

function ImageSeveritySummary({
  issues,
  hasMore,
}: {
  readonly issues: Issue[];
  readonly hasMore: boolean;
}) {
  const counts = new Map<string, number>();
  for (const iss of issues) {
    counts.set(iss.severity, (counts.get(iss.severity) ?? 0) + 1);
  }
  const severities = ['BLOCKER', 'CRITICAL', 'MAJOR', 'MINOR', 'INFO'].filter(
    (s) => (counts.get(s) ?? 0) > 0,
  );

  return (
    <div className="mb-4 flex items-center gap-2 flex-wrap">
      <span className="text-xs text-fg-subtle">Advisories by severity:</span>
      {severities.map((s) => (
        <span key={s} className={`orb-badge border ${severityTone(s)}`}>
          {s} · {counts.get(s)}
        </span>
      ))}
      <span className="ml-auto text-xs text-fg-subtle">
        {issues.length}
        {hasMore ? '+' : ''} findings
      </span>
    </div>
  );
}

interface ImageFindingRow {
  readonly issue: Issue;
  readonly baseImage: string;
  readonly packageLabel: string | null;
  readonly fixedVersion: string | null;
}

function ImagesList({
  issues,
  selectedID,
  onSelect,
}: {
  readonly issues: Issue[];
  readonly selectedID: string;
  readonly onSelect: (id: string) => void;
}) {
  const rows: ImageFindingRow[] = issues.map((issue) => {
    const info = parseScaMessage(issue.message);
    // container:vuln's Location.Path IS the image label already — there's
    // no Dockerfile/YAML source to anchor an ingest-driven finding to.
    // sca:image-base(-unknown) findings point file_path at the Dockerfile
    // instead, so the image ref only lives in the parsed message there.
    const baseImage =
      issue.rule_id === 'container:vuln' ? issue.file_path : info.baseImage ?? issue.file_path;
    const packageLabel = info.packageName
      ? `${info.packageName}${info.installedVersion ? `@${info.installedVersion}` : ''}`
      : null;
    return { issue, baseImage, packageLabel, fixedVersion: info.fixedVersion };
  });

  rows.sort((a, b) => {
    const rankDiff = (SEVERITY_RANK[b.issue.severity] ?? 0) - (SEVERITY_RANK[a.issue.severity] ?? 0);
    if (rankDiff !== 0) return rankDiff;
    return a.baseImage.localeCompare(b.baseImage);
  });

  return (
    <ul className="orb-card divide-y divide-border" aria-label="Image findings">
      {rows.map((row) => (
        <ImageFindingRowItem
          key={row.issue.id}
          row={row}
          selected={row.issue.id === selectedID}
          onSelect={onSelect}
        />
      ))}
    </ul>
  );
}

function ImageFindingRowItem({
  row,
  selected,
  onSelect,
}: {
  readonly row: ImageFindingRow;
  readonly selected: boolean;
  readonly onSelect: (id: string) => void;
}) {
  const { issue, baseImage, packageLabel, fixedVersion } = row;
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(issue.id)}
        aria-pressed={selected}
        aria-label={`Open issue: ${issue.message || issue.rule_id || issue.id}`}
        className={[
          'w-full text-left px-4 py-3 text-sm flex items-center gap-3 flex-wrap',
          'transition-colors hover:bg-bg-hover focus:outline-none focus:bg-bg-hover',
          'focus-visible:ring-2 focus-visible:ring-accent/40',
          selected ? 'bg-bg-hover' : '',
        ].join(' ')}
      >
        <span className={`orb-badge border shrink-0 ${severityTone(issue.severity)}`}>
          {issue.severity}
        </span>
        <span className="orb-code text-xs truncate max-w-[16rem]" title={baseImage}>
          {baseImage}
        </span>
        {packageLabel ? (
          <span className="text-xs text-fg-muted truncate max-w-[14rem]" title={packageLabel}>
            {packageLabel}
          </span>
        ) : null}
        <span className="text-xs text-fg flex-1 min-w-[10rem] truncate">{issue.message}</span>
        {fixedVersion ? (
          <span
            className="orb-badge bg-accent-muted text-accent border border-accent/30 shrink-0"
            title="Upgrade to this version to resolve"
          >
            fix: {fixedVersion}
          </span>
        ) : null}
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Permissions tab (T175)
// ---------------------------------------------------------------------------

const PROJ_ROLE_META: { key: ProjectPermissionRole; label: string; hint: string }[] = [
  { key: 'browse', label: 'Browse', hint: 'View measures, issues, comment' },
  { key: 'view_source', label: 'View Source', hint: 'Read source code excerpts' },
  { key: 'admin_issues', label: 'Administer Issues', hint: 'Resolve, change severity, mark false-positive' },
  { key: 'admin_project', label: 'Administer', hint: 'Project settings, integrations, delete' },
  { key: 'execute_analysis', label: 'Execute Analysis', hint: 'Push scan results (CI service accounts)' },
];

const PERMS_KEY = (projectID: string) =>
  `/api/v1/projects/${encodeURIComponent(projectID)}/permissions`;

function PermissionsTab({ projectID }: { readonly projectID: string }) {
  const grantsResult = useSWR<ProjectGrantEntry[]>(
    projectID ? PERMS_KEY(projectID) : null,
    fetcher,
  );

  // Load org users for the typeahead. Falls back gracefully on 403 (project_admin
  // doesn't have org_admin, so admin/users may 403 — we show a plain input instead).
  const [orgUsers, setOrgUsers] = useState<AdminUser[] | null>(null);
  const [usersError, setUsersError] = useState<ApiError | null>(null);
  const [usersLoaded, setUsersLoaded] = useState(false);

  // Lazy-load org users when the tab mounts (avoid fetching on every tab switch).
  useState(() => {
    if (usersLoaded) return;
    setUsersLoaded(true);
    adminUsersApi.list()
      .then((u) => setOrgUsers(u))
      .catch((e: unknown) => {
        if (e instanceof ApiError) setUsersError(e);
      });
  });

  const refreshGrants = () => {
    void swrMutate(PERMS_KEY(projectID));
  };

  return (
    <Panel id="panel-permissions" labelledBy="tab-permissions">
      {grantsResult.isLoading ? <Loading label="Loading permissions…" /> : null}
      {grantsResult.error && !grantsResult.isLoading && grantsResult.error instanceof ApiError && grantsResult.error.status === 403 && (
        <div className="orb-card p-8 text-center">
          <div className="text-fg-subtle inline-flex items-center justify-center mb-2">
            <Lock className="h-5 w-5" aria-hidden="true" />
          </div>
          <h2 className="font-display text-lg font-semibold text-fg mb-2">
            Access denied
          </h2>
          <p className="text-fg-muted text-sm max-w-md mx-auto">
            Managing project permissions requires the{' '}
            <span className="orb-code">admin_project</span> role on this
            project, or the <span className="orb-code">org_admin</span> org role.
          </p>
        </div>
      )}
      {grantsResult.error && !grantsResult.isLoading && !(grantsResult.error instanceof ApiError && grantsResult.error.status === 403) && (
        <RequestError error={grantsResult.error} />
      )}
      {!grantsResult.isLoading && !grantsResult.error ? (
        <div className="flex flex-col gap-6">
          <AddMemberSection
            projectID={projectID}
            orgUsers={orgUsers}
            usersError={usersError}
            existingGrants={grantsResult.data ?? []}
            onGranted={refreshGrants}
          />
          <CurrentGrantsSection
            projectID={projectID}
            grants={grantsResult.data ?? []}
            onChanged={refreshGrants}
          />
        </div>
      ) : null}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Add Member section
// ---------------------------------------------------------------------------

interface AddMemberSectionProps {
  readonly projectID: string;
  readonly orgUsers: AdminUser[] | null;
  readonly usersError: ApiError | null;
  readonly existingGrants: ProjectGrantEntry[];
  readonly onGranted: () => void;
}

function AddMemberSection({
  projectID,
  orgUsers,
  usersError,
  existingGrants,
  onGranted,
}: AddMemberSectionProps) {
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  // For the fallback (org_user list unavailable): manual user_id entry.
  const [manualID, setManualID] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<Set<ProjectPermissionRole>>(
    new Set(),
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const noUserList = usersError !== null;

  const filteredUsers =
    orgUsers && search.trim()
      ? orgUsers.filter(
          (u) =>
            u.name.toLowerCase().includes(search.toLowerCase()) ||
            u.email.toLowerCase().includes(search.toLowerCase()),
        )
      : [];

  const toggleRole = (role: ProjectPermissionRole) => {
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(role)) next.delete(role);
      else next.add(role);
      return next;
    });
  };

  const targetUserID = noUserList ? manualID.trim() : selectedUser?.id ?? '';
  const canGrant = targetUserID !== '' && selectedRoles.size > 0 && !saving;

  const handleGrant = async () => {
    if (!canGrant) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      for (const role of selectedRoles) {
        await projectPermissionsApi.grant(projectID, targetUserID, role);
      }
      setSaveSuccess(true);
      setSearch('');
      setSelectedUser(null);
      setManualID('');
      setSelectedRoles(new Set());
      onGranted();
      // Clear success indicator after a moment.
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch (e: unknown) {
      setSaveError(formatErrorMessage(e, 'Grant failed'));
    } finally {
      setSaving(false);
    }
  };

  // Check if a user already holds any role (used to de-emphasise already-added users).
  const existingUserIDs = new Set(existingGrants.map((g) => g.user_id));

  return (
    <div className="orb-card p-5">
      <div className="flex items-center gap-2 mb-4">
        <UserPlus className="h-4 w-4 text-fg-subtle" aria-hidden="true" />
        <h2 className="font-display text-sm font-semibold text-fg">Add member</h2>
      </div>

      {/* User search / ID input */}
      {noUserList ? (
        <div className="mb-4">
          <label className="block text-xs text-fg-muted mb-1.5">
            User ID
            {usersError?.status === 403 ? (
              <span className="ml-2 text-fg-subtle">
                (org_admin required to browse users — enter ID directly)
              </span>
            ) : null}
          </label>
          <input
            type="text"
            placeholder="UUID of the user to grant"
            value={manualID}
            onChange={(e) => setManualID(e.target.value)}
            className="
              w-full text-sm bg-bg-elevated border border-border rounded px-3 py-1.5
              text-fg placeholder:text-fg-subtle
              focus:outline-none focus:ring-1 focus:ring-accent/50
            "
          />
        </div>
      ) : (
        <div className="mb-4 relative">
          <label htmlFor="perm-search" className="block text-xs text-fg-muted mb-1.5">
            Search by name or email
          </label>
          <input
            id="perm-search"
            type="text"
            placeholder={orgUsers ? 'Type to search org members…' : 'Loading users…'}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setSelectedUser(null);
            }}
            disabled={!orgUsers}
            className="
              w-full text-sm bg-bg-elevated border border-border rounded px-3 py-1.5
              text-fg placeholder:text-fg-subtle
              focus:outline-none focus:ring-1 focus:ring-accent/50
              disabled:opacity-50
            "
          />
          {selectedUser ? (
            <div className="mt-1.5 flex items-center gap-2 text-sm text-fg">
              <UserAvatar name={selectedUser.name} />
              <span>{selectedUser.name}</span>
              <span className="text-fg-muted text-xs">{selectedUser.email}</span>
              {existingUserIDs.has(selectedUser.id) ? (
                <span className="orb-badge bg-bg-inset text-fg-muted border border-border text-xs">
                  already added
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => { setSelectedUser(null); setSearch(''); }}
                className="ml-auto text-fg-subtle hover:text-fg"
                aria-label="Clear selected user"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : null}
          {/* Typeahead dropdown */}
          {!selectedUser && filteredUsers.length > 0 ? (
            <ul className="
              absolute z-10 mt-1 w-full bg-bg-elevated border border-border
              rounded shadow-lg max-h-48 overflow-y-auto
            ">
              {filteredUsers.slice(0, 8).map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => { setSelectedUser(u); setSearch(''); }}
                    className="
                      w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left
                      hover:bg-bg-hover focus:outline-none focus:bg-bg-hover
                    "
                  >
                    <UserAvatar name={u.name} />
                    <span className="font-medium text-fg">{u.name}</span>
                    <span className="text-fg-muted text-xs">{u.email}</span>
                    {existingUserIDs.has(u.id) ? (
                      <span className="ml-auto text-xs text-fg-subtle">(member)</span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}

      {/* Role checkboxes */}
      <div className="mb-4">
        <p className="text-xs text-fg-muted mb-2">Roles to grant</p>
        <div className="flex flex-wrap gap-3">
          {PROJ_ROLE_META.map((r) => (
            <label
              key={r.key}
              title={r.hint}
              className="
                flex items-center gap-1.5 text-sm cursor-pointer
                text-fg-muted hover:text-fg select-none
              "
            >
              <input
                type="checkbox"
                checked={selectedRoles.has(r.key)}
                onChange={() => toggleRole(r.key)}
                className="accent-accent"
              />
              {r.label}
            </label>
          ))}
        </div>
      </div>

      {/* Grant button + feedback */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          disabled={!canGrant}
          onClick={() => { void handleGrant(); }}
          className="
            inline-flex items-center gap-2 px-4 py-1.5 rounded text-xs font-semibold
            uppercase tracking-[0.08em]
            bg-accent text-accent-fg hover:opacity-90
            border border-accent/40 transition-opacity
            disabled:opacity-40 disabled:cursor-not-allowed
          "
        >
          {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
          {!saving && saveSuccess && <Check className="h-3.5 w-3.5" aria-hidden="true" />}
          {!saving && !saveSuccess && <UserPlus className="h-3.5 w-3.5" aria-hidden="true" />}
          {saving && 'Granting…'}
          {!saving && saveSuccess && 'Granted'}
          {!saving && !saveSuccess && 'Grant'}
        </button>
        {saveError ? (
          <p className="text-xs text-danger">{saveError}</p>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Current Grants section
// ---------------------------------------------------------------------------

function CurrentGrantsSection({
  projectID,
  grants,
  onChanged,
}: {
  readonly projectID: string;
  readonly grants: ProjectGrantEntry[];
  readonly onChanged: () => void;
}) {
  return (
    <div className="orb-card">
      <div className="flex items-center gap-2 p-5 border-b border-border">
        <Users className="h-4 w-4 text-fg-subtle" aria-hidden="true" />
        <h2 className="font-display text-sm font-semibold text-fg">
          Current grants
        </h2>
        <span className="ml-auto text-xs text-fg-muted">{grants.length} member{grants.length === 1 ? '' : 's'}</span>
      </div>
      {grants.length === 0 ? (
        <div className="p-6 text-center">
          <p className="text-sm text-fg-muted">
            No project-specific grants yet. Use "Add member" to grant roles.
          </p>
        </div>
      ) : (
        <>
          {/* Column headers */}
          <div className="hidden sm:grid grid-cols-[auto_1fr_repeat(5,auto)_auto] gap-3 px-5 py-2 border-b border-border text-xs text-fg-subtle">
            <span /> {/* avatar */}
            <span>User</span>
            {PROJ_ROLE_META.map((r) => (
              <span key={r.key} className="text-center" title={r.hint}>
                {r.label}
              </span>
            ))}
            <span /> {/* revoke button */}
          </div>
          <ul className="divide-y divide-border">
            {grants.map((g) => (
              <GrantRow
                key={g.user_id}
                grant={g}
                projectID={projectID}
                onChanged={onChanged}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function GrantRow({
  grant,
  projectID,
  onChanged,
}: {
  readonly grant: ProjectGrantEntry;
  readonly projectID: string;
  readonly onChanged: () => void;
}) {
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const handleRevokeAll = async () => {
    if (revoking) return;
    setRevoking(true);
    setRevokeError(null);
    try {
      for (const role of grant.roles) {
        await projectPermissionsApi.revoke(projectID, grant.user_id, role);
      }
      onChanged();
    } catch (e: unknown) {
      let revokeErrMsg: string;
      if (e instanceof ApiError) {
        revokeErrMsg = `${e.code}: ${e.message}`;
      } else if (e instanceof Error) {
        revokeErrMsg = e.message;
      } else {
        revokeErrMsg = 'Revoke failed';
      }
      setRevokeError(revokeErrMsg);
    } finally {
      setRevoking(false);
    }
  };

  const grantedSet = new Set(grant.roles);
  const displayName = grant.name || grant.user_id.slice(0, 8);

  return (
    <li className="px-5 py-3 flex flex-col gap-2 sm:grid sm:grid-cols-[auto_1fr_repeat(5,auto)_auto] sm:items-center sm:gap-3">
      {/* Avatar */}
      <UserAvatar name={displayName} />

      {/* Name + email */}
      <div className="min-w-0">
        <p className="text-sm text-fg font-medium truncate">{displayName}</p>
        {grant.email ? (
          <p className="text-xs text-fg-muted truncate">{grant.email}</p>
        ) : null}
      </div>

      {/* 5 role chips */}
      {PROJ_ROLE_META.map((r) => (
        <div key={r.key} className="flex justify-center" title={r.hint}>
          {grantedSet.has(r.key) ? (
            <span
              className="orb-badge bg-accent-muted text-accent border border-accent/30"
              aria-label={`${r.label}: granted`}
            >
              <Check className="h-3 w-3" />
            </span>
          ) : (
            <span
              className="orb-badge bg-bg-inset text-fg-subtle border border-border"
              aria-label={`${r.label}: not granted`}
            >
              <X className="h-3 w-3" />
            </span>
          )}
        </div>
      ))}

      {/* Revoke all */}
      <div className="flex flex-col items-end gap-1">
        <button
          type="button"
          disabled={revoking}
          onClick={() => { void handleRevokeAll(); }}
          title="Revoke all roles for this user on this project"
          className="
            inline-flex items-center gap-1 px-2 py-1 rounded text-xs
            text-fg-muted border border-border
            hover:border-danger/50 hover:text-danger
            disabled:opacity-40 disabled:cursor-not-allowed
            transition-colors
          "
        >
          {revoking ? (
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
          ) : (
            <X className="h-3 w-3" aria-hidden="true" />
          )}
          {revoking ? 'Revoking…' : 'Revoke all'}
        </button>
        {revokeError ? (
          <p className="text-xs text-danger">{revokeError}</p>
        ) : null}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Settings tab — new-code period configuration (T254)
// ---------------------------------------------------------------------------

const PERIOD_TYPE_OPTIONS = [
  { value: '', label: 'PR diff only (default)' },
  { value: 'previous_version', label: 'Since previous version' },
  { value: 'days', label: 'Last N days' },
  { value: 'reference_branch', label: 'Compared to reference branch' },
] as const;

function SettingsTab({
  projectID,
  project,
  projectKey,
}: {
  readonly projectID: string;
  readonly project: Project | undefined;
  readonly projectKey: string | null;
}) {
  const [periodType, setPeriodType] = useState<string>(
    project?.new_code_period_type ?? '',
  );
  const [periodValue, setPeriodValue] = useState<string>(
    project?.new_code_period_value ?? '',
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const patch: ProjectPatch = {
        new_code_period_type: periodType,
        new_code_period_value: periodType === '' ? '' : periodValue,
      };
      await projectsApi.patch(projectID, patch);
      if (projectKey) void swrMutate(projectKey);
      setSaved(true);
    } catch (e: unknown) {
      let saveErrMsg: string;
      if (e instanceof ApiError) {
        saveErrMsg = `${e.code}: ${e.message}`;
      } else if (e instanceof Error) {
        saveErrMsg = e.message;
      } else {
        saveErrMsg = 'Save failed';
      }
      setSaveError(saveErrMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel id="panel-settings" labelledBy="tab-settings">
      <div className="orb-card p-6 max-w-xl">
        <h2 className="font-display text-base font-semibold text-fg mb-1">
          New-code period
        </h2>
        <p className="text-sm text-fg-muted mb-5">
          Controls which issues count as "new" in the quality gate. Aligns with
          SonarQube&apos;s Clean as You Code approach.
        </p>

        <label htmlFor="period-type" className="block mb-1 text-xs font-semibold text-fg-subtle uppercase tracking-wide">
          Period type
        </label>
        <select
          id="period-type"
          value={periodType}
          onChange={(e) => {
            setPeriodType(e.target.value);
            setPeriodValue('');
            setSaved(false);
          }}
          className="w-full orb-input mb-4"
        >
          {PERIOD_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        {periodType === 'days' && (
          <div className="mb-4">
            <label htmlFor="period-days" className="block mb-1 text-xs font-semibold text-fg-subtle uppercase tracking-wide">
              Number of days
            </label>
            <input
              id="period-days"
              type="number"
              min={1}
              max={365}
              placeholder="30"
              value={periodValue}
              onChange={(e) => { setPeriodValue(e.target.value); setSaved(false); }}
              className="w-full orb-input"
            />
          </div>
        )}
        {periodType === 'reference_branch' && (
          <div className="mb-4">
            <label htmlFor="period-branch" className="block mb-1 text-xs font-semibold text-fg-subtle uppercase tracking-wide">
              Reference branch name
            </label>
            <input
              id="period-branch"
              type="text"
              placeholder="main"
              value={periodValue}
              onChange={(e) => { setPeriodValue(e.target.value); setSaved(false); }}
              className="w-full orb-input"
            />
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="orb-btn-primary"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {saved ? (
            <span className="flex items-center gap-1 text-sm text-success">
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
              Saved
            </span>
          ) : null}
          {saveError ? (
            <span className="text-sm text-danger">{saveError}</span>
          ) : null}
        </div>
      </div>

      <div className="mt-6">
        <TokensSection projectID={projectID} />
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Tokens section (T705) — project-scoped scan tokens. This is the primary
// onboarding fix: create project → get a token → run the scanner → see
// results, all from the browser, no curl/DB access required.
// ---------------------------------------------------------------------------

const PROJECT_TOKENS_KEY = (projectID: string) =>
  `/api/v1/projects/${encodeURIComponent(projectID)}/tokens`;

function TokensSection({ projectID }: { readonly projectID: string }) {
  const { data, error, isLoading, mutate } = useSWR<ProjectToken[]>(
    projectID ? PROJECT_TOKENS_KEY(projectID) : null,
    fetcher,
  );

  // The freshly-created token's plaintext, shown once. Cleared on dismiss —
  // never re-derivable from `data` (the list response never carries it).
  const [freshToken, setFreshToken] = useState<ProjectToken | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const handleCreated = (token: ProjectToken) => {
    setShowCreate(false);
    setFreshToken(token);
    void mutate();
  };

  const handleRevoke = async (tokenID: string) => {
    await projectTokensApi.revoke(projectID, tokenID);
    void mutate();
  };

  if (isLoading) {
    return (
      <div className="orb-card p-6 flex items-center gap-3 text-fg-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span className="text-sm">Loading tokens…</span>
      </div>
    );
  }

  if (error) {
    if (error instanceof ApiError && error.status === 403) {
      return (
        <div className="orb-card p-8 text-center">
          <div className="text-fg-subtle inline-flex items-center justify-center mb-2">
            <Lock className="h-5 w-5" aria-hidden="true" />
          </div>
          <h2 className="font-display text-lg font-semibold text-fg mb-2">
            Access denied
          </h2>
          <p className="text-fg-muted text-sm max-w-md mx-auto">
            Managing scan tokens requires the{' '}
            <span className="orb-code">admin_project</span> role on this
            project, or the <span className="orb-code">org_admin</span> org role.
          </p>
        </div>
      );
    }
    return <RequestError error={error} />;
  }

  const tokens = data ?? [];

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-1">
        <h2 className="font-display text-base font-semibold text-fg flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-fg-subtle" aria-hidden="true" />
          Scan tokens
        </h2>
        {!showCreate && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="
              inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
              text-xs font-semibold uppercase tracking-[0.08em]
              bg-accent text-accent-fg hover:opacity-90
              border border-accent/40 transition-opacity
            "
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            New token
          </button>
        )}
      </div>
      <p className="text-sm text-fg-muted mb-4 max-w-xl">
        Authenticates <span className="orb-code">orbital-lint-scanner</span> runs
        against this project. The plaintext is shown once at creation — store it
        in your CI secrets.
      </p>

      {freshToken?.plaintext ? (
        <TokenCopyOnceBanner
          projectID={projectID}
          token={freshToken}
          onDismiss={() => setFreshToken(null)}
        />
      ) : null}

      {showCreate ? (
        <CreateProjectTokenForm
          projectID={projectID}
          existingNames={new Set(tokens.filter((t) => !t.revoked).map((t) => t.name))}
          onCreated={handleCreated}
          onCancel={() => setShowCreate(false)}
        />
      ) : null}

      {tokens.length === 0 ? (
        <div className="orb-card p-6 text-center">
          <p className="text-sm text-fg-muted">
            No tokens yet. Create one to run your first scan.
          </p>
        </div>
      ) : (
        <ProjectTokenTable tokens={tokens} onRevoke={handleRevoke} />
      )}
    </div>
  );
}

function TokenCopyOnceBanner({
  projectID,
  token,
  onDismiss,
}: {
  readonly projectID: string;
  readonly token: ProjectToken;
  readonly onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const plaintext = token.plaintext ?? '';
  // --commit is required in server mode (main.go's runServerMode rejects a
  // scan without one) and the scanner does not shell out to git to infer it
  // itself — verified live: the INSTALL.md quickstart snippet omits it too
  // and fails identically when copy-pasted verbatim. $(git rev-parse HEAD)
  // keeps this command actually runnable as-is from a repo checkout.
  const command = `orbital-lint-scanner --server ${window.location.origin} --token ${plaintext} --project ${projectID} --commit $(git rev-parse HEAD) --path . --wait`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(plaintext);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      role="alert"
      className="orb-card mb-4 p-5 border-success/40 bg-success/5 space-y-3"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-fg">
            Token "{token.name}" created — copy it now
          </p>
          <p className="text-xs text-fg-muted mt-0.5">
            This value will not be shown again.
          </p>
        </div>
        <button
          onClick={onDismiss}
          className="p-1 rounded hover:bg-bg-subtle text-fg-muted hover:text-fg transition-colors"
          title="Dismiss"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      <div className="flex items-center gap-3">
        <code className="flex-1 font-mono text-xs bg-bg-inset border border-border rounded px-3 py-2 truncate text-fg select-all">
          {plaintext}
        </code>
        <button
          onClick={() => void handleCopy()}
          className="
            inline-flex items-center gap-1.5 px-4 py-2 rounded-md shrink-0
            text-xs font-semibold uppercase tracking-[0.08em]
            bg-accent text-accent-fg hover:opacity-90
            border border-accent/40 transition-opacity
          "
        >
          {copied ? (
            <><Check className="h-3.5 w-3.5" aria-hidden="true" /> Copied</>
          ) : (
            <><ClipboardCopy className="h-3.5 w-3.5" aria-hidden="true" /> Copy</>
          )}
        </button>
      </div>
      <div>
        <p className="text-xs text-fg-muted mb-1">Ready to scan:</p>
        <code className="block font-mono text-xs bg-bg-inset border border-border rounded px-3 py-2 text-fg overflow-x-auto whitespace-pre select-all">
          {command}
        </code>
      </div>
    </div>
  );
}

function CreateProjectTokenForm({
  projectID,
  existingNames,
  onCreated,
  onCancel,
}: {
  readonly projectID: string;
  readonly existingNames: Set<string>;
  readonly onCreated: (token: ProjectToken) => void;
  readonly onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    const trimmed = name.trim();
    if (!trimmed) { setErr('Token name is required.'); return; }
    if (existingNames.has(trimmed)) { setErr('An active token with this name already exists.'); return; }
    setSaving(true);
    try {
      const created = await projectTokensApi.create(projectID, { name: trimmed });
      setName('');
      onCreated(created);
    } catch (e: unknown) {
      setErr(formatErrorMessage(e, 'Failed to create token'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="orb-card mb-4 p-5 space-y-4">
      <p className="text-sm font-semibold text-fg">New scan token</p>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="proj-token-name" className="orb-section-title text-[11px]">
          Token name
        </label>
        <input
          id="proj-token-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="orb-input"
          placeholder="e.g. github-actions, local-dev"
          autoFocus
        />
      </div>
      {err && <p role="alert" className="text-xs text-danger">{err}</p>}
      <div className="flex items-center gap-3">
        <button type="submit" disabled={saving} className="orb-btn-primary">
          {saving ? (
            <><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Creating…</>
          ) : (
            'Create token'
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-fg-muted hover:text-fg transition-colors px-2"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function ProjectTokenTable({
  tokens,
  onRevoke,
}: {
  readonly tokens: ProjectToken[];
  readonly onRevoke: (tokenID: string) => void | Promise<void>;
}) {
  const [revoking, setRevoking] = useState<string | null>(null);

  // Newest first, matching ScansList/BranchesList convention.
  const sorted = [...tokens].sort((a, b) => b.created_at.localeCompare(a.created_at));

  const handleRevoke = async (t: ProjectToken) => {
    if (!confirm(`Revoke token "${t.name}"? Scans using it will be rejected immediately.`)) return;
    setRevoking(t.id);
    try {
      await onRevoke(t.id);
    } finally {
      setRevoking(null);
    }
  };

  return (
    <Table>
      <THead>
        <TR>
          <TH>Name</TH>
          <TH>Last used</TH>
          <TH>Created</TH>
          <TH srOnly>Actions</TH>
        </TR>
      </THead>
      <TBody>
          {sorted.map((t) => (
            <TR key={t.id} muted={t.revoked}>
              <TD primary>
                {t.name}
                {t.revoked && (
                  <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-danger/70">
                    revoked
                  </span>
                )}
              </TD>
              <TD mono>
                {t.last_used ? formatTimestamp(t.last_used) : <span className="italic">never</span>}
              </TD>
              <TD mono>
                {formatTimestamp(t.created_at)}
              </TD>
              <TD align="right">
                {!t.revoked && (
                  <button
                    onClick={() => void handleRevoke(t)}
                    disabled={revoking === t.id}
                    className="p-1.5 rounded text-fg-muted hover:text-danger hover:bg-danger/10 transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-30"
                    title={`Revoke token "${t.name}"`}
                  >
                    {revoking === t.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                  </button>
                )}
              </TD>
            </TR>
          ))}
      </TBody>
    </Table>
  );
}

/** Render a circular avatar with up to 2 initials from the display name. */
function UserAvatar({ name }: { readonly name: string }) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <span
      className="
        inline-flex items-center justify-center h-7 w-7 rounded-full shrink-0
        bg-accent/15 text-accent text-xs font-semibold select-none
      "
      aria-hidden="true"
    >
      {initials || '?'}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Measures Dashboard (T209) — SonarQube-style project overview
// ---------------------------------------------------------------------------

/** Map an A–E rating to a Tailwind class set. Returns '' when no rating yet. */
function ratingClasses(r: string): string {
  switch (r) {
    case 'A': return 'bg-accent/20 text-accent border-accent/40';
    case 'B': return 'bg-accent/10 text-accent border-accent/20';
    case 'C': return 'bg-warning/20 text-warning border-warning/40';
    case 'D': return 'bg-warning/10 text-warning border-warning/20';
    case 'E': return 'bg-danger/20 text-danger border-danger/40';
    default:  return 'bg-bg-inset text-fg-muted border-border';
  }
}

function coverageClass(pct: number): string {
  if (pct >= 80) return 'text-accent';
  if (pct >= 60) return 'text-warning';
  return 'text-danger';
}

function dupClass(pct: number): string {
  if (pct < 3) return 'text-accent';
  if (pct < 10) return 'text-warning';
  return 'text-danger';
}

/** Format a remediation-effort value in minutes into a human label. */
function formatDebt(minutes: number): string {
  if (minutes <= 0) return '0min';
  if (minutes < 60) return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  if (h < 8) return `${h}h`;
  const d = Math.floor(h / 8);
  if (d < 5) return `${d}d`;
  return `${Math.floor(d / 5)}w`;
}

interface RatingRowProps {
  readonly reliability: string;
  readonly security: string;
  readonly maintain: string;
  readonly bugs: number;
  readonly vulns: number;
  readonly smells: number;
  readonly debt: number;
}

function RatingRow({ reliability, security, maintain, bugs, vulns, smells, debt }: RatingRowProps) {
  const bugSub = `${bugs} bug${bugs === 1 ? '' : 's'}`;
  const vulnSub = `${vulns} vuln${vulns === 1 ? '' : 's'}`;
  const maintainSub = debt > 0 ? `${formatDebt(debt)} debt` : `${smells} smell${smells === 1 ? '' : 's'}`;
  return (
    <div className="grid grid-cols-3 gap-3">
      <RatingCell icon={<Bug className="h-4 w-4" />} label="Reliability" rating={reliability} sub={bugSub} />
      <RatingCell icon={<ShieldAlert className="h-4 w-4" />} label="Security" rating={security} sub={vulnSub} />
      <RatingCell icon={<Wrench className="h-4 w-4" />} label="Maintainability" rating={maintain} sub={maintainSub} />
    </div>
  );
}

interface MeasuresDashboardProps {
  readonly scanID: string;
}

function MeasuresDashboard({ scanID }: MeasuresDashboardProps) {
  const key = `/api/v1/scans/${encodeURIComponent(scanID)}/measures`;
  const { data, isLoading } = useSWR<ScanMeasures>(key, fetcher);
  const [mode, setMode] = useState<'overall' | 'new'>('overall');

  if (isLoading) {
    return (
      <div className="mb-6 orb-card p-5 flex items-center gap-3 text-fg-muted">
        <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden="true" />
        <span className="text-sm">Loading measures…</span>
      </div>
    );
  }
  if (!data) return null;

  const isNew = mode === 'new';

  const reliability = isNew ? data.new_reliability_rating : data.reliability_rating;
  const security    = isNew ? data.new_security_rating    : data.security_rating;
  const maintain    = isNew ? data.new_maintainability_rating : data.maintainability_rating;
  const debt        = isNew ? data.new_technical_debt     : data.technical_debt;
  const sqale       = isNew ? data.new_sqale_debt_ratio   : data.sqale_debt_ratio;
  const bugs        = isNew ? data.new_bugs               : data.bugs;
  const vulns       = isNew ? data.new_vulnerabilities    : data.vulnerabilities;
  const smells      = isNew ? data.new_code_smells        : data.code_smells;
  const cov         = isNew ? data.new_coverage           : data.coverage;
  const covLines    = isNew ? data.new_lines_to_cover     : data.lines_to_cover;
  const covUncov    = isNew ? data.new_uncovered_lines    : data.uncovered_lines;
  const dupDensity  = isNew ? data.new_duplicated_lines_density : data.duplicated_lines_density;
  const dupLines    = isNew ? data.new_duplicated_lines   : data.duplicated_lines;
  const issuesTotal = isNew ? data.new_issues_total       : data.issues_total;

  const hasRatings = reliability || security || maintain;

  return (
    <div className="mb-6 orb-card p-5 space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-accent shrink-0" aria-hidden="true" />
          <h2 className="font-display text-sm font-semibold text-fg">Measures</h2>
        </div>
        {/* Overall / New Code toggle */}
        <fieldset
          aria-label="Code scope"
          className="inline-flex rounded border border-border overflow-hidden text-xs font-semibold"
        >
          <legend className="sr-only">Code scope</legend>
          {(['overall', 'new'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setMode(v)}
              className={[
                'px-3 py-1',
                mode === v
                  ? 'bg-accent text-accent-fg'
                  : 'bg-bg-elevated text-fg-muted hover:text-fg hover:bg-bg-hover transition-colors',
              ].join(' ')}
            >
              {v === 'new' ? 'New Code' : 'Overall'}
            </button>
          ))}
        </fieldset>
      </div>

      {/* T617: license-enforcement notes — e.g. deep taint or custom rule
          findings skipped for lack of a license. Empty on a fully-licensed scan. */}
      {data.license_notes && data.license_notes.length > 0 ? (
        <div
          role="status"
          className="flex items-start gap-2 rounded border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning"
        >
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
          <ul className="space-y-0.5">
            {data.license_notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* T700: analysis completeness notes — e.g. the deep-taint engine
          hitting its budget before reaching a fixpoint. Not a licensing
          decision (that's license_notes above); empty on a scan that
          completed every stage. */}
      {data.analysis_notes && data.analysis_notes.length > 0 ? (
        <div
          role="status"
          className="flex items-start gap-2 rounded border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning"
        >
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
          <ul className="space-y-0.5">
            {data.analysis_notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* A–E Rating row */}
      {hasRatings ? (
        <RatingRow
          reliability={reliability}
          security={security}
          maintain={maintain}
          bugs={bugs}
          vulns={vulns}
          smells={smells}
          debt={debt}
        />
      ) : (
        <p className="text-xs text-fg-muted italic">
          A–E ratings will appear after the first complete scan populates metrics.
        </p>
      )}

      {/* Secondary metrics grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <MeasureCell
          icon={<Wrench className="h-3.5 w-3.5" />}
          label="Technical Debt"
          value={debt > 0 ? formatDebt(debt) : '—'}
          sub={sqale > 0 ? `${sqale.toFixed(1)}% of dev cost` : undefined}
        />
        <MeasureCell
          icon={<Radar className="h-3.5 w-3.5" />}
          label="Coverage"
          value={covLines > 0 ? `${cov.toFixed(1)}%` : '—'}
          sub={covLines > 0 ? `${covLines - covUncov}/${covLines} lines` : 'no coverage data'}
          valueClass={covLines > 0 ? coverageClass(cov) : undefined}
        />
        <MeasureCell
          icon={<Copy className="h-3.5 w-3.5" />}
          label="Duplication"
          value={data.duplicated_blocks > 0 ? `${dupDensity.toFixed(1)}%` : '—'}
          sub={data.duplicated_blocks > 0 ? `${dupLines} dup. lines` : 'no dup. data'}
          valueClass={data.duplicated_blocks > 0 ? dupClass(dupDensity) : undefined}
        />
        <MeasureCell
          icon={<Activity className="h-3.5 w-3.5" />}
          label="Complexity"
          value={data.complexity > 0 ? data.complexity.toLocaleString() : '—'}
          sub={data.cognitive_complexity > 0 ? `cogn: ${data.cognitive_complexity.toLocaleString()}` : undefined}
        />
      </div>

      {/* Size + total issues footer */}
      <div className="flex flex-wrap gap-4 text-xs text-fg-muted pt-1 border-t border-border">
        <span>
          <span className="font-semibold text-fg">{data.loc.toLocaleString()}</span>{' '}
          lines of code
        </span>
        <span>
          <span className="font-semibold text-fg">{data.files_analyzed.toLocaleString()}</span>{' '}
          files
        </span>
        <span>
          <span className={`font-semibold ${issuesTotal > 0 ? 'text-danger' : 'text-fg'}`}>
            {issuesTotal.toLocaleString()}
          </span>{' '}
          issues {isNew ? '(new code)' : '(overall)'}
        </span>
      </div>
    </div>
  );
}

interface RatingCellProps {
  readonly icon: ReactNode;
  readonly label: string;
  readonly rating: string;
  readonly sub: string;
}

function RatingCell({ icon, label, rating, sub }: RatingCellProps) {
  const cls = ratingClasses(rating);
  return (
    <div className="flex flex-col items-center gap-2 p-4 rounded-md bg-bg-inset border border-border text-center">
      <div className="flex items-center gap-1 text-xs text-fg-subtle">
        {icon}
        <span>{label}</span>
      </div>
      <span
        className={`inline-flex items-center justify-center w-11 h-11 rounded-full text-2xl font-bold border ${cls}`}
        aria-label={`${label} rating: ${rating || 'not available'}`}
      >
        {rating || '—'}
      </span>
      <span className="text-xs text-fg-muted">{sub}</span>
    </div>
  );
}

interface MeasureCellProps {
  readonly icon: ReactNode;
  readonly label: string;
  readonly value: string;
  readonly sub?: string;
  readonly valueClass?: string;
}

function MeasureCell({ icon, label, value, sub, valueClass }: MeasureCellProps) {
  return (
    <div className="flex flex-col gap-1 p-3 rounded-md bg-bg-inset border border-border">
      <div className="flex items-center gap-1.5 text-fg-subtle text-xs">
        <span className="h-3.5 w-3.5 shrink-0" aria-hidden="true">{icon}</span>
        <span className="truncate">{label}</span>
      </div>
      <span className={`font-display text-lg font-semibold ${valueClass ?? 'text-fg'}`}>
        {value}
      </span>
      {sub ? <span className="text-xs text-fg-muted">{sub}</span> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-directory smell density (T592)
// ---------------------------------------------------------------------------

/** Matches db.MaxIssueListLimit — the same cap HotspotsToReviewLink (T341)
 *  uses for a "count without paginating a whole second list UI" fetch. */
const SMELL_DENSITY_LIMIT = 200;
/** How many directory rows to render before folding the rest into a note. */
const SMELL_DENSITY_TOP_N = 8;

interface DirectorySmellCount {
  readonly directory: string;
  readonly count: number;
}

/** Group CODE_SMELL issues by their containing directory (the file_path
 *  minus its final segment), sorted by count descending. This is a count
 *  distribution, not a true LOC-normalized density — per-directory LOC isn't
 *  available to the frontend — so the panel copy is careful to say "share of
 *  code smells", not "density per line", to avoid overclaiming precision. */
function groupByDirectory(issues: Issue[]): DirectorySmellCount[] {
  const counts = new Map<string, number>();
  for (const issue of issues) {
    const path = issue.file_path;
    const slash = path.lastIndexOf('/');
    const dir = slash >= 0 ? path.slice(0, slash) : '(root)';
    counts.set(dir, (counts.get(dir) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([directory, count]) => ({ directory, count }))
    .sort((a, b) => b.count - a.count);
}

function SmellDensityPanel({
  projectID,
  branchId,
}: {
  readonly projectID: string;
  readonly branchId: string;
}) {
  const key = projectID
    ? buildIssuesURL({
        project_id: projectID,
        branch_id: branchId || undefined,
        kind: 'CODE_SMELL',
        limit: SMELL_DENSITY_LIMIT,
      })
    : null;
  const { data, isLoading } = useSWR<IssueListResponse>(key, fetcher);

  if (isLoading) {
    return (
      <div className="mb-6 orb-card p-5 flex items-center gap-3 text-fg-muted">
        <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden="true" />
        <span className="text-sm">Loading smell density…</span>
      </div>
    );
  }

  const issues = data?.items ?? [];
  if (issues.length === 0) return null;

  const byDirectory = groupByDirectory(issues);
  const topDirs = byDirectory.slice(0, SMELL_DENSITY_TOP_N);
  const maxCount = topDirs[0]?.count ?? 1;
  // The fetch is capped at SMELL_DENSITY_LIMIT, so a non-empty next_cursor
  // means the true distribution includes smells we never saw — flagged
  // explicitly rather than silently presenting a partial count as complete
  // (the T441 "no eyeballing" lesson applies to UI copy too).
  const capped = Boolean(data?.next_cursor);

  return (
    <div className="mb-6 orb-card p-5 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <FolderTree className="h-4 w-4 text-accent shrink-0" aria-hidden="true" />
          <h2 className="font-display text-sm font-semibold text-fg">
            Code smells by directory
          </h2>
        </div>
        <span className="text-xs text-fg-subtle">
          {issues.length}
          {capped ? '+' : ''} code smell{issues.length === 1 ? '' : 's'}
        </span>
      </div>
      <ul className="space-y-1.5">
        {topDirs.map((d) => (
          <li key={d.directory} className="flex items-center gap-3">
            <span
              className="orb-code text-xs text-fg-muted truncate max-w-[16rem] shrink-0"
              title={d.directory}
            >
              {d.directory}
            </span>
            <div className="flex-1 h-2 rounded bg-bg-inset overflow-hidden">
              <div
                className="h-full bg-accent/60"
                style={{ width: `${Math.max(4, Math.round((d.count / maxCount) * 100))}%` }}
              />
            </div>
            <span className="text-xs text-fg-muted w-10 text-right shrink-0">
              {d.count}
            </span>
          </li>
        ))}
      </ul>
      {byDirectory.length > topDirs.length ? (
        <p className="text-xs text-fg-subtle">
          + {byDirectory.length - topDirs.length} more director
          {byDirectory.length - topDirs.length === 1 ? 'y' : 'ies'} with smells.
        </p>
      ) : null}
      {capped ? (
        <p className="text-xs text-fg-subtle italic">
          Based on the first {SMELL_DENSITY_LIMIT} code smells found — the
          true distribution may differ once more are loaded.
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function Panel({
  id,
  labelledBy,
  children,
}: {
  readonly id: string;
  readonly labelledBy: string;
  readonly children: ReactNode;
}) {
  return (
    <section role="tabpanel" id={id} aria-labelledby={labelledBy}>
      {children}
    </section>
  );
}

function Loading({ label }: { readonly label: string }) {
  return (
    <div className="orb-card p-6 flex items-center justify-center gap-3 text-fg-muted">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

function RequestError({ error }: { readonly error: unknown }) {
  let msg: string;
  if (error instanceof ApiError) {
    msg = `${error.code}: ${error.message}`;
  } else if (error instanceof Error) {
    msg = error.message;
  } else {
    msg = 'Unknown error';
  }
  return (
    <div role="alert" className="orb-card p-5 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-1">request failed</p>
      <p className="text-sm text-fg">{msg}</p>
    </div>
  );
}

function EmptyState({
  icon,
  title,
  body,
}: {
  readonly icon: ReactNode;
  readonly title: string;
  readonly body: ReactNode;
}) {
  return (
    <div className="orb-card p-8 text-center">
      <div className="text-fg-subtle inline-flex items-center justify-center mb-2">
        {icon}
      </div>
      <h2 className="font-display text-lg font-semibold text-fg mb-2">
        {title}
      </h2>
      <p className="text-fg-muted text-sm max-w-md mx-auto">{body}</p>
    </div>
  );
}

/**
 * Format an ISO-8601 timestamp as a compact local string. We deliberately
 * avoid Intl.RelativeTimeFormat ("3 hours ago") here — those strings drift
 * on long-open tabs and Phase-1 doesn't ship a periodic re-render.
 */
function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
